import { Linking } from 'react-native';
import { useRouter } from 'expo-router';
import { Text, YStack } from 'tamagui';

/**
 * Shared layout primitives for the legal screens (`/privacy`, `/terms`).
 * Extracted so the two documents render identically — a restyle (link
 * colour, bullet glyph, line-height) happens in one place instead of
 * drifting across copy-pasted per-screen helpers.
 */

export function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <YStack gap={10}>
      <Text fontSize={18} fontWeight="700" color="$textOnSurface">
        {title}
      </Text>
      {children}
    </YStack>
  );
}

export function Subsection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <YStack gap={6} marginTop={6}>
      <Text fontSize={15} fontWeight="700" color="$textOnSurface">
        {title}
      </Text>
      {children}
    </YStack>
  );
}

export function Paragraph({ children }: { children: React.ReactNode }) {
  return (
    <Text fontSize={14} color="$textOnSurface" lineHeight={22}>
      {children}
    </Text>
  );
}

export function BulletList({ items }: { items: string[] }) {
  return (
    <YStack gap={6} paddingLeft={4}>
      {items.map((text) => (
        <Text key={text} fontSize={14} color="$textOnSurface" lineHeight={22}>
          {'•  '}
          {text}
        </Text>
      ))}
    </YStack>
  );
}

export function ExternalLink({ url, label }: { url: string; label: string }) {
  // Inline Tamagui Text with onPress so the link sits inside a parent
  // paragraph. RN bubbles onPress on the innermost Text node; a Pressable
  // inside Text would detach the link from the surrounding line.
  return (
    <Text
      color="$accentCerulean"
      textDecorationLine="underline"
      onPress={() => void Linking.openURL(url)}
      accessibilityRole="link"
      accessibilityLabel={label}
    >
      {label}
    </Text>
  );
}

export function InternalLink({ url, label }: { url: string; label: string }) {
  // In-app route (e.g. /privacy) — navigate via expo-router so it works on
  // web and native alike. `Linking.openURL('/privacy')` would fail on native
  // (no scheme/host).
  const router = useRouter();
  return (
    <Text
      color="$accentCerulean"
      textDecorationLine="underline"
      onPress={() => router.push(url)}
      accessibilityRole="link"
      accessibilityLabel={label}
    >
      {label}
    </Text>
  );
}
